import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Plus,
  Edit3,
  Calendar,
  Mail,
  FileText,
  Rocket,
  ArrowRight,
  LayoutDashboard,
  CheckCircle2,
  Clock,
  AlertCircle,
  TrendingUp,
  Loader2,
  Users,
  ExternalLink,
} from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import { API } from "../apiBase";
import { toast } from "sonner";

/**
 * AdminDashboard — `/admin` Vue générale.
 * --------------------------------------
 * Aggregates data from existing admin endpoints:
 *   - GET /api/admin/blog/posts
 *   - GET /api/demo-requests
 *   - GET /api/admin/pro-users
 *   - GET /api/admin/studio/cdc
 *
 * Renders:
 *   - 4 key metrics (with delta this month / week)
 *   - 2 main columns: recent articles, upcoming demos
 *   - 2 secondary columns: services status, quick actions
 */
export default function AdminDashboard() {
  const [posts, setPosts] = useState([]);
  const [demos, setDemos] = useState([]);
  const [pros, setPros] = useState([]);
  const [studio, setStudio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushingPostId, setPushingPostId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [p, d, u, s] = await Promise.all([
          axios.get(`${API}/admin/blog/posts`).then((r) => r.data).catch(() => []),
          axios.get(`${API}/demo-requests`).then((r) => r.data).catch(() => []),
          axios.get(`${API}/admin/pro-users`).then((r) => r.data).catch(() => []),
          axios.get(`${API}/admin/studio/cdc`).then((r) => r.data).catch(() => []),
        ]);
        if (!alive) return;
        setPosts(Array.isArray(p) ? p : []);
        setDemos(Array.isArray(d) ? d : []);
        setPros(Array.isArray(u) ? u : []);
        setStudio(Array.isArray(s) ? s : []);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // --- Metric computation ---------------------------------------------------
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const startOfWeek = (() => {
    const d = new Date(now);
    d.setDate(d.getDate() - d.getDay()); // Sunday
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  })();
  const tsOf = (v) => {
    if (!v) return 0;
    const t = typeof v === "string" ? Date.parse(v) : Number(v);
    return isFinite(t) ? t : 0;
  };

  const publishedPosts = posts.filter((p) => p.status === "published");
  const postsThisMonth = publishedPosts.filter((p) => tsOf(p.published_at || p.created_at) >= startOfMonth).length;

  const upcomingDemos = demos
    .filter((d) => {
      const t = tsOf(d.preferred_at || d.created_at);
      return t >= now.getTime() - 1000 * 60 * 60; // include "now-ish"
    })
    .sort((a, b) => tsOf(a.preferred_at || a.created_at) - tsOf(b.preferred_at || b.created_at));
  const demosThisWeek = demos.filter((d) => tsOf(d.created_at) >= startOfWeek).length;

  const prosThisMonth = pros.filter((u) => tsOf(u.created_at) >= startOfMonth).length;
  const studioThisMonth = studio.filter((s) => tsOf(s.created_at) >= startOfMonth).length;

  const metrics = [
    {
      key: "articles",
      Icon: FileText,
      label: "Articles publiés",
      value: publishedPosts.length,
      delta: postsThisMonth,
      deltaLabel: "ce mois",
      tint: "mint",
    },
    {
      key: "demos",
      Icon: Calendar,
      label: "Démos planifiées",
      value: upcomingDemos.length,
      delta: demosThisWeek,
      deltaLabel: "cette semaine",
      tint: "blue",
    },
    {
      key: "accounts",
      Icon: Users,
      label: "Comptes créés",
      value: pros.length,
      delta: prosThisMonth,
      deltaLabel: "ce mois",
      tint: "blue",
    },
    {
      key: "studio",
      Icon: LayoutDashboard,
      label: "Projets Studio",
      value: studio.length,
      delta: studioThisMonth,
      deltaLabel: "ce mois",
      tint: "mint",
    },
  ];

  const recentArticles = publishedPosts
    .slice()
    .sort((a, b) => tsOf(b.published_at || b.created_at) - tsOf(a.published_at || a.created_at))
    .slice(0, 3);

  const nextDemos = upcomingDemos.slice(0, 4);

  const onPushPost = async (postId, title) => {
    setPushingPostId(postId);
    try {
      const { data } = await axios.post(`${API}/admin/blog/posts/${postId}/indexnow-push`);
      if (data?.status === "ok") {
        toast.success(`« ${title} » re-poussé à IndexNow`);
      } else {
        toast.warning(`Statut : ${data?.code || data?.error || "inconnu"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du push IndexNow");
    } finally {
      setPushingPostId(null);
    }
  };

  const onIndexNowSitemap = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/indexnow/notify-sitemap`);
      if (data?.status === "ok") {
        toast.success(
          `${data.submitted} URL${data.submitted > 1 ? "s" : ""} envoyée${data.submitted > 1 ? "s" : ""} à Bing / Yandex / Naver / Seznam`
        );
      } else {
        toast.warning(`Statut : ${data?.code || data?.error || "inconnu"}`);
      }
    } catch (e) {
      toast.error("Échec de la notification IndexNow");
    }
  };

  return (
    <AdminPageShell
      title="Tableau de bord"
      subtitle="Vue d'ensemble de votre espace MetaConnect"
      tab="overview"
      seoTitle="Admin — Tableau de bord MetaConnect"
      seoCanonical="/admin"
    >
      {/* METRICS */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="admin-metrics">
        {metrics.map((m) => (
          <MetricCard key={m.key} {...m} loading={loading} />
        ))}
      </section>

      {/* MAIN ROW — Articles + Demos */}
      <section className="grid lg:grid-cols-2 gap-5 mb-5">
        {/* Recent articles */}
        <Panel
          title="Articles récents"
          icon={FileText}
          footer={
            <Link
              to="/admin/blog/nouveau"
              data-testid="admin-overview-new-article"
              className="btn-primary px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-2"
            >
              <Plus size={14} /> Nouvel article
            </Link>
          }
          testid="panel-recent-articles"
        >
          {loading ? (
            <PanelSkeleton rows={3} />
          ) : recentArticles.length === 0 ? (
            <EmptyState icon={FileText} label="Aucun article publié pour le moment." />
          ) : (
            <ul className="divide-y divide-[rgba(127,179,255,0.1)]">
              {recentArticles.map((p) => (
                <li
                  key={p.id}
                  className="py-3 flex items-start justify-between gap-3"
                  data-testid={`overview-article-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/admin/edit/${p.id}`}
                      className="text-[#0F2B5B] text-[13.5px] font-medium leading-snug line-clamp-2 hover:text-[var(--mc-mint)] transition"
                    >
                      {p.title}
                    </Link>
                    <div className="text-[11.5px] text-[var(--mc-muted)] mt-1 inline-flex items-center gap-1.5">
                      <Calendar size={11} />
                      {new Date(p.published_at || p.created_at).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => onPushPost(p.id, p.title)}
                      disabled={pushingPostId === p.id}
                      data-testid={`overview-indexnow-${p.id}`}
                      title="Pousser à IndexNow"
                      className="grid place-items-center h-8 w-8 rounded-full border border-[rgba(127,179,255,0.22)] text-[#0284C7] hover:text-[#0F2B5B] hover:border-[var(--mc-blue-400)] disabled:opacity-50 transition"
                    >
                      {pushingPostId === p.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Rocket size={12} />
                      )}
                    </button>
                    <Link
                      to={`/admin/edit/${p.id}`}
                      data-testid={`overview-edit-${p.id}`}
                      title="Éditer"
                      className="grid place-items-center h-8 w-8 rounded-full border border-[rgba(127,179,255,0.22)] text-[#0284C7] hover:text-[#0F2B5B] hover:border-[var(--mc-blue-400)] transition"
                    >
                      <Edit3 size={12} />
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* Upcoming demos */}
        <Panel
          title="Prochaines démos"
          icon={Calendar}
          footer={
            <Link
              to="/admin/demos/nouvelle"
              data-testid="admin-overview-new-demo"
              className="btn-primary px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-2"
            >
              <Plus size={14} /> Planifier une démo
            </Link>
          }
          testid="panel-upcoming-demos"
        >
          {loading ? (
            <PanelSkeleton rows={4} />
          ) : nextDemos.length === 0 ? (
            <EmptyState icon={Calendar} label="Aucune démo planifiée." />
          ) : (
            <ul className="divide-y divide-[rgba(127,179,255,0.1)]">
              {nextDemos.map((d) => (
                <DemoRow key={d.id} demo={d} />
              ))}
            </ul>
          )}
        </Panel>
      </section>

      {/* SECONDARY ROW — Services status + Quick actions */}
      <section className="grid lg:grid-cols-2 gap-5">
        <Panel title="Statut des services" icon={CheckCircle2} testid="panel-services-status">
          <ul className="divide-y divide-[rgba(127,179,255,0.1)]">
            <ServiceRow label="Hébergement HDS" tone="ok" text="Opérationnel" />
            <ServiceRow label="MSSanté" tone="ok" text="Opérationnel" />
            <ServiceRow label="SEO IndexNow" tone="ok" text="Actif" />
            <ServiceRow label="Pro Santé Connect" tone="soon" text="Bientôt disponible" />
            <ServiceRow label="DMP / FHIR" tone="soon" text="Bientôt disponible" />
          </ul>
        </Panel>

        <Panel title="Actions rapides" icon={Rocket} testid="panel-quick-actions">
          <div className="grid grid-cols-2 gap-2.5">
            <QuickAction
              to="/admin/blog/nouveau"
              testid="quick-new-article"
              Icon={FileText}
              title="Nouvel article"
              sub="Blog SEO"
            />
            <QuickAction
              to="/mssante/creer-boite"
              testid="quick-new-mssante"
              Icon={Mail}
              title="Boîte MSSanté"
              sub="Messagerie sécurisée"
            />
            <QuickAction
              to="/studio"
              testid="quick-studio"
              Icon={LayoutDashboard}
              title="Studio"
              sub="Configurateur"
            />
            <QuickAction
              onClick={onIndexNowSitemap}
              testid="quick-indexnow"
              Icon={Rocket}
              title="Pousser IndexNow"
              sub="SEO & indexation"
            />
          </div>
        </Panel>
      </section>
    </AdminPageShell>
  );
}

// =========================================================================
//  Building blocks
// =========================================================================

function MetricCard({ Icon, label, value, delta, deltaLabel, tint, loading }) {
  const tintClasses =
    tint === "mint"
      ? "bg-[rgba(110,231,197,0.10)] border-[rgba(110,231,197,0.32)] text-[var(--mc-mint)]"
      : "bg-[rgba(45,123,255,0.14)] border-[rgba(127,179,255,0.36)] text-[#0284C7]";
  return (
    <div
      className="mc-card p-4 lg:p-5"
      data-testid={`metric-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className={`inline-grid place-items-center h-9 w-9 rounded-lg border ${tintClasses}`}>
        <Icon size={16} />
      </div>
      <div className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[var(--mc-muted)]">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="font-display text-[#0F2B5B] text-3xl leading-none">
          {loading ? "…" : value}
        </div>
        {!loading && delta > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-[var(--mc-mint)]">
            <TrendingUp size={11} /> +{delta}
          </span>
        )}
      </div>
      <div className="text-[11px] text-[var(--mc-muted)] mt-0.5">{deltaLabel}</div>
    </div>
  );
}

function Panel({ title, icon: Icon, children, footer, testid }) {
  return (
    <div className="mc-card p-5 lg:p-6 flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <span className="grid place-items-center h-8 w-8 rounded-lg bg-[rgba(127,179,255,0.08)] border border-[rgba(127,179,255,0.18)] text-[#0284C7]">
          <Icon size={14} />
        </span>
        <h2 className="text-[#0F2B5B] text-[15px] font-semibold tracking-tight">{title}</h2>
      </div>
      <div className="flex-1">{children}</div>
      {footer && (
        <div className="mt-5 pt-4 border-t border-[rgba(127,179,255,0.1)]">{footer}</div>
      )}
    </div>
  );
}

function PanelSkeleton({ rows = 3 }) {
  return (
    <ul className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="animate-pulse">
          <div className="h-4 bg-[rgba(127,179,255,0.08)] rounded w-3/4 mb-2" />
          <div className="h-3 bg-[rgba(127,179,255,0.05)] rounded w-1/3" />
        </li>
      ))}
    </ul>
  );
}

function EmptyState({ icon: Icon, label }) {
  return (
    <div className="py-8 text-center">
      <Icon size={22} className="mx-auto text-[#0284C7] opacity-60" />
      <p className="mt-2 text-[12.5px] text-[var(--mc-muted)]">{label}</p>
    </div>
  );
}

function DemoRow({ demo }) {
  const when = demo.preferred_at || demo.created_at;
  const d = when ? new Date(when) : null;
  const statusMap = {
    confirmed: { tone: "ok", label: "Confirmée" },
    new: { tone: "warn", label: "En attente" },
    contacted: { tone: "warn", label: "En attente" },
    won: { tone: "ok", label: "Confirmée" },
  };
  const s = statusMap[demo.status] || { tone: "warn", label: "En attente" };
  return (
    <li
      className="py-3 flex items-center gap-3 flex-wrap"
      data-testid={`overview-demo-${demo.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[#0F2B5B] text-[13.5px] font-medium truncate">
          {demo.full_name || demo.name || demo.email || "Prospect"}
        </div>
        <div className="text-[11.5px] text-[var(--mc-muted)] mt-0.5 inline-flex items-center gap-2">
          {d && (
            <>
              <Calendar size={11} />
              {d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
              <Clock size={11} />
              {d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </>
          )}
        </div>
      </div>
      <StatusBadge tone={s.tone} label={s.label} />
    </li>
  );
}

function StatusBadge({ tone, label }) {
  const cls =
    tone === "ok"
      ? "border-[rgba(110,231,197,0.4)] text-[var(--mc-mint)] bg-[rgba(110,231,197,0.06)]"
      : tone === "warn"
      ? "border-[rgba(255,170,80,0.4)] text-[#B45309] bg-[rgba(255,170,80,0.06)]"
      : tone === "soon"
      ? "border-[rgba(127,179,255,0.32)] text-[#0284C7] bg-[rgba(45,123,255,0.08)]"
      : tone === "err"
      ? "border-[rgba(255,120,120,0.4)] text-[#DC2626] bg-[rgba(255,120,120,0.06)]"
      : "border-[rgba(127,179,255,0.22)] text-[var(--mc-muted)]";
  const Icon = tone === "ok" ? CheckCircle2 : tone === "warn" ? AlertCircle : tone === "soon" ? Clock : null;
  return (
    <span
      className={`text-[11px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 ${cls}`}
    >
      {Icon && <Icon size={11} />}
      {label}
    </span>
  );
}

function ServiceRow({ label, tone, text }) {
  return (
    <li className="py-2.5 flex items-center justify-between gap-3">
      <span className="text-[#334155] text-[13px]">{label}</span>
      <StatusBadge tone={tone} label={text} />
    </li>
  );
}

function QuickAction({ to, onClick, Icon, title, sub, testid }) {
  const inner = (
    <>
      <div className="grid place-items-center h-9 w-9 rounded-lg bg-[rgba(110,231,197,0.10)] border border-[rgba(110,231,197,0.3)] text-[var(--mc-mint)] mb-3">
        <Icon size={16} />
      </div>
      <div className="text-[#0F2B5B] text-[13px] font-medium leading-tight">{title}</div>
      <div className="text-[11px] text-[var(--mc-muted)] mt-0.5">{sub}</div>
      <ArrowRight
        size={13}
        className="absolute top-3.5 right-3.5 text-[var(--mc-muted)] group-hover:text-[#0F2B5B] group-hover:translate-x-0.5 transition"
      />
    </>
  );
  const cls =
    "group relative rounded-xl border border-[rgba(127,179,255,0.18)] bg-[#F8FAFC] p-4 hover:border-[var(--mc-blue-400)] hover:bg-[rgba(45,123,255,0.06)] transition block";
  if (to) {
    return (
      <Link to={to} data-testid={testid} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <button onClick={onClick} data-testid={testid} className={`${cls} text-left w-full`}>
      {inner}
    </button>
  );
}
